import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Pagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as ActivateIcon,
  Stop as CloseIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventsService } from '../services/api';
import EventForm from '../components/Events/EventForm';
import EventParticipants from '../components/Events/EventParticipants';
import EventFilters from '../components/Events/EventFilters';

const AdminEvents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Get default status based on user role
  const getDefaultStatus = () => {
    if (user?.role === 'admin') {
      return 'active';  // Admins default to active events
    } else if (user?.role === 'judge') {
      return 'closed';  // Judges default to closed events
    }
    return 'all';  // Fallback for other roles
  };
  
  // Filter state with role-based defaults
  const [filters, setFilters] = useState({
    status: getDefaultStatus(),
    year: 'all',
    search: '',
    limit: 6  // Default to 6 events per page
  });
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  
  // API response data
  const [eventCounts, setEventCounts] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  
  // Dialog states
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuEvent, setMenuEvent] = useState(null);

  // Update filter defaults when user role becomes available
  useEffect(() => {
    if (user?.role) {
      const defaultStatus = getDefaultStatus();
      setFilters(prev => ({
        ...prev,
        status: defaultStatus
      }));
    }
  }, [user?.role]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (user?.role === 'admin') {
        loadEvents();
      }
    }, filters.search ? 300 : 0); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
  }, [user, filters, page]);

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  // Helper functions for time display
  const getTimeUntilEnd = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'ended';
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    return `in ${diffDays} days`;
  };

  const getTimeUntilStart = (startDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const diffTime = start - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'started';
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    return `in ${diffDays} days`;
  };

  const getTimeSinceEnd = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = now - end;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      
      // Calculate offset for pagination
      const offset = (page - 1) * filters.limit;
      
      // Build query parameters based on filters
      const queryParams = {};
      if (filters.status !== 'all') {
        queryParams.status = filters.status;
      }
      if (filters.year !== 'all') {
        queryParams.year = filters.year;
      }
      if (filters.search.trim()) {
        queryParams.search = filters.search.trim();
      }
      queryParams.limit = filters.limit;
      queryParams.offset = offset;
      
      const response = await eventsService.getEvents(queryParams);
      console.log('Admin Events API response:', response);
      
      const responseData = response.data.data;
      setEvents(responseData.events || []);
      setTotalEvents(responseData.total || responseData.events?.length || 0);
      
      // Set counts and available years from API response
      setEventCounts(responseData.status_counts || {});
      setAvailableYears(responseData.available_years || []);
      
    } catch (err) {
      setError('Failed to load events');
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setEventFormOpen(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setEventFormOpen(true);
    handleMenuClose();
  };

  const handleDeleteEvent = (event) => {
    setSelectedEvent(event);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleViewParticipants = (event) => {
    setSelectedEvent(event);
    setParticipantsOpen(true);
    handleMenuClose();
  };

  const handleManageResults = (event) => {
    navigate(`/admin/events/${event.event_id}/results`);
    handleMenuClose();
  };

  const handleStatusChange = async (event, newStatus) => {
    try {
      await eventsService.updateEvent(event.event_id, { status: newStatus });
      setSuccess(`Event ${newStatus} successfully`);
      loadEvents();
    } catch (err) {
      setError(`Failed to ${newStatus} event`);
      console.error('Error updating event status:', err);
    }
    handleMenuClose();
  };

  const handleEventSaved = () => {
    setEventFormOpen(false);
    setSelectedEvent(null);
    loadEvents();
    setSuccess(selectedEvent ? 'Event updated successfully' : 'Event created successfully');
  };

  const handleDeleteConfirm = async () => {
    try {
      await eventsService.deleteEvent(selectedEvent.event_id);
      setDeleteConfirmOpen(false);
      setSelectedEvent(null);
      setSuccess('Event deleted successfully');
      loadEvents();
    } catch (err) {
      setError('Failed to delete event');
      console.error('Error deleting event:', err);
    }
  };

  const handleMenuOpen = (event, eventData) => {
    setAnchorEl(event.currentTarget);
    setMenuEvent(eventData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuEvent(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'warning';
      case 'active': return 'success';
      case 'closed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusActions = (event) => {
    const actions = [];
    
    switch (event.status) {
      case 'upcoming':
        actions.push({ label: 'Activate', action: () => handleStatusChange(event, 'active'), icon: <ActivateIcon /> });
        actions.push({ label: 'Cancel', action: () => handleStatusChange(event, 'cancelled'), icon: <CancelIcon /> });
        break;
      case 'active':
        actions.push({ label: 'Close', action: () => handleStatusChange(event, 'closed'), icon: <CloseIcon /> });
        actions.push({ label: 'Cancel', action: () => handleStatusChange(event, 'cancelled'), icon: <CancelIcon /> });
        break;
      default:
        break;
    }
    
    return actions;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Event Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateEvent}
          size="large"
        >
          Create Event
        </Button>
      </Box>

      {/* Event Filters */}
      <EventFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        eventCounts={eventCounts}
        availableYears={availableYears}
        loading={loading}
      />

      {/* Compact Status Legend */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
          Events
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexWrap: 'wrap'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mr: 1 }}>
            Status:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'success.main' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Active</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'warning.main' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Upcoming</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'info.main' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Closed</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'grey.400' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Archived</Typography>
          </Box>
        </Box>
      </Box>

      {/* Events Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading events...
          </Typography>
        </Box>
      ) : events.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {filters.status !== 'all' || filters.search || filters.year !== 'all' 
                ? 'No events match your filters' 
                : 'No events found'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {filters.status !== 'all' || filters.search || filters.year !== 'all'
                ? 'Try adjusting your filters or create a new event'
                : 'Create your first event to get started'
              }
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateEvent}
            >
              Create Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {events.map((event) => (
            <Grid item xs={12} md={6} lg={4} key={event.event_id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 2,
                    cursor: 'pointer'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Title Row with Status Dot and Menu */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    {/* Status Dot with Tooltip */}
                    <Tooltip 
                      title={
                        event.status === 'active' ? 'Active Event - Currently running' :
                        event.status === 'upcoming' ? 'Upcoming Event - Starting soon' :
                        event.status === 'closed' ? 'Closed Event - Recently ended' :
                        'Archived Event - Older event'
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 
                            event.status === 'active' ? 'success.main' :
                            event.status === 'upcoming' ? 'warning.main' :
                            event.status === 'closed' ? 'info.main' :
                            'grey.400',
                          mr: 1.5,
                          mt: 0.75,
                          flexShrink: 0,
                          cursor: 'help'
                        }}
                      />
                    </Tooltip>
                    
                    {/* Event Title */}
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      sx={{ 
                        flexGrow: 1, 
                        mr: 1,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        lineHeight: 1.3
                      }}
                    >
                      {event.title}
                    </Typography>
                    
                    {/* Menu Button */}
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, event)}
                      sx={{ 
                        opacity: 0.7,
                        '&:hover': { opacity: 1 }
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Theme and Deadline */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2, ml: 2.5 }}
                  >
                    {event.theme && `${event.theme} • `}
                    {event.status === 'active' && `Ends ${getTimeUntilEnd(event.end_date)}`}
                    {event.status === 'upcoming' && `Starts ${getTimeUntilStart(event.start_date)}`}
                    {event.status === 'closed' && `Ended ${getTimeSinceEnd(event.end_date)}`}
                  </Typography>

                  {/* Stats Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 2.5 }}>
                    {/* Participants */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {event.participant_count || 0} participants
                      </Typography>
                    </Box>
                    
                    {/* Submissions (for active/closed events) */}
                    {(event.status === 'active' || event.status === 'closed') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          📝 {event.submission_count || 0} submissions
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Word Count (only if set) */}
                  {(event.min_word_count || event.max_word_count) && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ mt: 1, ml: 2.5, fontSize: '0.875rem' }}
                    >
                      📏 {
                        event.min_word_count && event.max_word_count 
                          ? `${event.min_word_count}-${event.max_word_count} words`
                          : event.min_word_count 
                            ? `Min ${event.min_word_count} words`
                            : `Max ${event.max_word_count} words`
                      }
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination Controls */}
      {!loading && events.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {((page - 1) * filters.limit) + 1} to {Math.min(page * filters.limit, totalEvents)} of {totalEvents} events
          </Typography>
          {Math.ceil(totalEvents / filters.limit) > 1 && (
            <Pagination
              count={Math.ceil(totalEvents / filters.limit)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditEvent(menuEvent)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Event</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleViewParticipants(menuEvent)}>
          <ListItemIcon>
            <PeopleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Participants</ListItemText>
        </MenuItem>

        {/* Results Management - only for closed events */}
        {menuEvent && menuEvent.status === 'closed' && (
          <>
            <MenuItem onClick={() => handleManageResults(menuEvent)}>
              <ListItemIcon>
                <TrophyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage Results</ListItemText>
            </MenuItem>
            
            {/* View Public Results - only if results are published */}
            {menuEvent.results_published && (
              <MenuItem onClick={() => {
                navigate(`/events/${menuEvent.event_id}/results`);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <TrophyIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>View Public Results</ListItemText>
              </MenuItem>
            )}
          </>
        )}

        {/* Status Actions */}
        {menuEvent && getStatusActions(menuEvent).map((action, index) => (
          <MenuItem key={index} onClick={action.action}>
            <ListItemIcon>
              {action.icon}
            </ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}

        <MenuItem onClick={() => handleDeleteEvent(menuEvent)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Event</ListItemText>
        </MenuItem>
      </Menu>

      {/* Event Form Dialog */}
      <Dialog
        open={eventFormOpen}
        onClose={() => setEventFormOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedEvent ? 'Edit Event' : 'Create New Event'}
        </DialogTitle>
        <DialogContent>
          <EventForm
            event={selectedEvent}
            onSave={handleEventSaved}
            onCancel={() => setEventFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog
        open={participantsOpen}
        onClose={() => setParticipantsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Event Participants - {selectedEvent?.title}
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <EventParticipants eventId={selectedEvent.event_id} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParticipantsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedEvent?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminEvents;
